import { useGlobals }                                                                                       from '@/hooks/use-globals';
import { blankValidator, loginCharsValidator, containWhitespacesValidator, emailValidator, loginValidator } from '@/helpers/validators';
import { AddUserForm, EditPasswordForm, EditUserForm, ModalMode, ServerError }                              from '@/views/users/users.types';
import { Rules }                                                                                            from '@/helpers/validators.types';

export default function (rules: AddUserForm | EditUserForm | EditPasswordForm, mode: ModalMode) {
  const { store, i18n } = useGlobals();

  return {
    firstName: [
      {
        validator: blankValidator(i18n.t('vUsers.formRules.firstName.blank') as string),
        trigger: 'submit',
      },
      {
        required: true,
        message: i18n.t('vUsers.formRules.firstName.required') as string,
        trigger: 'submit',
      },
      {
        max: 32,
        message: i18n.t('vUsers.formRules.firstName.characters') as string,
        trigger: 'submit',
      },
    ],
    lastName: [
      {
        validator: blankValidator(i18n.t('vUsers.formRules.lastName.blank') as string),
        trigger: 'submit',
      },
      {
        required: true,
        message: i18n.t('vUsers.formRules.lastName.required') as string,
        trigger: 'submit',
      },
      {
        max: 32,
        message: i18n.t('vUsers.formRules.lastName.characters') as string,
        trigger: 'submit',
      },
    ],
    position: [
      {
        validator: blankValidator(i18n.t('vUsers.formRules.position.blank') as string),
        trigger: 'submit',
      },
      {
        max: 64,
        message: i18n.t('vUsers.formRules.position.characters') as string,
        trigger: 'submit',
      },
    ],
    status: [
      {
        required: true,
        message: i18n.t('vUsers.formRules.status.required') as string,
        trigger: 'submit',
      },
      {
        validator: (rule: Rules, value: string, callback: (error?: Error) => void) => {
          if (![ 'ACTIVE', 'INACTIVE' ].includes(value.toUpperCase())) {
            callback(new Error(i18n.t('vUsers.formRules.status.value') as string));
          } else {
            callback();
          }
        },
        trigger: 'submit',
      },
    ],
    email: [
      {
        validator: emailValidator,
        trigger: 'submit',
      },
      {
        validator: (rule: Rules, value: string, callback: (error?: Error) => void) => {
          const emailErrors: ServerError[] = store.state.views.vUsers.serverErrors.filter((error: ServerError) => error.field === rule?.field);
          if (emailErrors.length) {
            emailErrors.forEach((error: ServerError) => {
              callback(new Error(i18n.t(`vUsers.formRules.email.${error.reason}`) as string));
            });
          } else {
            callback();
          }
        },
        trigger: 'submit',
      },
      {
        required: true,
        message: i18n.t('vUsers.formRules.email.required' as string),
        trigger: 'submit',
      },
      {
        max: 64,
        message: i18n.t('vUsers.formRules.email.characters' as string),
        trigger: 'submit',
      },
    ],
    login: [
      {
        validator: loginValidator(i18n.t('vUsers.formRules.login.whitespaces') as string),
        trigger: 'submit',
      },
      {
        validator: (rule: Rules, value: string, callback: (error?: Error) => void) => {
          const loginErrors: ServerError[] = store.state.views.vUsers.serverErrors.filter((error: ServerError) => error.field === rule?.field);
          if (loginErrors.length) {
            loginErrors.forEach((error: ServerError) => {
              callback(new Error(i18n.t(`vUsers.formRules.login.${error.reason}`) as string));
            });
          } else {
            callback();
          }
        },
        trigger: 'submit',
      },
      {
        required: true,
        message: i18n.t('vUsers.formRules.login.required') as string,
        trigger: 'submit',
      },
      {
        max: 64,
        message: i18n.t('vUsers.formRules.login.characters') as string,
        trigger: 'submit',
      },
      {
        validator: loginCharsValidator(),
        trigger: 'submit',
      },
    ],
    password: [
      {
        validator: containWhitespacesValidator(i18n.t('vUsers.formRules.password.whitespaces') as string),
        trigger: 'submit',
      },
      {
        required: mode !== ModalMode.EDIT_USER,
        message: i18n.t('vUsers.formRules.password.required') as string,
        trigger: 'submit',
      },
      {
        max: 16,
        min: 3,
        message: i18n.t('vUsers.formRules.password.characters') as string,
        trigger: 'submit',
      },
    ],
    repeatPassword: [
      {
        validator: (rule: Rules, value: string, callback: (error?: Error) => void) => {
          if ('password' in rules) {
            const { password, repeatPassword } = store.state.views.vUsers.modal.form.fields;
            if (password !== repeatPassword) {
              callback(new Error(i18n.t('vUsers.formRules.confirmPassword.match') as string));
            } else {
              callback();
            }
          } else {
            callback();
          }
        },
        trigger: 'submit',
      },
      {
        required: mode !== ModalMode.EDIT_USER,
        message: i18n.t('vUsers.formRules.confirmPassword.required') as string,
        trigger: 'submit',
      },
    ],
  };
}
